import { useEffect, useRef, useState } from "react";
import * as api from "../api/client";
import type { SleepState, WorldNode } from "../api/client";
import { E32 } from "../theme/endesga32";
import { pixelTheme } from "../theme/pixel";
import { bevelShadow } from "../theme/styles";
import { HeartIcon } from "./HeartIcon";

interface Props {
  /** World-tree node for the inn — supplies the flavor description
   *  (authored in `world/world.yaml`, not hardcoded). */
  node: Extract<WorldNode, { kind: "nav" }>;
  /** Initial sleep snapshot from /api/me. The component animates
   *  live HP locally; on sleep / wake it round-trips to /api/inn/* */
  sleep: SleepState;
  /** Called whenever the local sleep state changes — parent can
   *  update its cached `User.sleep` (so AppHeader's HP chip stays in
   *  sync without an extra /me fetch). */
  onSleepChange: (next: SleepState) => void;
}

// The Inn page. Sleeping locks /world navigation to this page (the
// server enforces it; we just render the controls). HP regenerates
// at 1 per `regen_seconds_per_hp` real seconds while sleeping.
//
// No polling: the server stamps `sleep_started_at` + `sleep_start_hp`
// once and the client computes live HP from that snapshot via
// `api.liveHp` on a 250ms interval. Wake on the user's click sends
// `POST /api/inn/wake` to commit the earned HP server-side.
//
// The sleep control IS the HP bar — clicking it toggles sleep, and
// the bar fills left-to-right as HP regenerates.
export function InnView({ node, sleep, onSleepChange }: Props) {
  const theme = pixelTheme;
  const [state, setState] = useState<SleepState>(sleep);
  const [hp, setHp] = useState(() => api.liveHp(sleep));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    setState(sleep);
    setHp(api.liveHp(sleep));
  }, [sleep]);

  useEffect(() => {
    if (!state.sleeping) return;

    const id = window.setInterval(() => {
      setHp(api.liveHp(stateRef.current));
    }, 250);

    return () => window.clearInterval(id);
  }, [state.sleeping]);

  const atFull = hp >= state.max_hp;
  const fillPct = Math.max(0, Math.min(100, (hp / state.max_hp) * 100));

  // The button does double duty as a toggle and as the HP bar.
  // - Awake at full HP: disabled, "Fully rested".
  // - Awake with damage: "Sleep" — click starts sleep.
  // - Sleeping: "Stop sleeping" — click commits earned HP.
  const buttonLabel = state.sleeping
    ? "Stop sleeping"
    : atFull
      ? "Fully rested"
      : "Sleep";
  const buttonDisabled = (atFull && !state.sleeping) || busy;

  async function onToggle() {
    if (buttonDisabled) return;
    setBusy(true);
    setError(null);
    try {
      const r = state.sleeping ? await api.innWake() : await api.innSleep();
      setState(r.sleep);
      onSleepChange(r.sleep);
    } catch (e) {
      setError(
        e instanceof api.ApiError && e.message
          ? e.message
          : state.sleeping
            ? "Could not stop sleeping."
            : "Could not start sleeping.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        display: "grid",
        gap: 16,
        padding: 16,
        border: `2px solid ${theme.panelEdge}`,
        background: theme.panel,
        boxShadow: bevelShadow,
      }}
    >
      {node.description && (
        <div
          style={{
            fontSize: 16,
            lineHeight: 1.5,
            color: theme.ink,
            whiteSpace: "pre-wrap",
          }}
        >
          {node.description.trim()}
        </div>
      )}

      <button
        type="button"
        onClick={(e) => {
          if (buttonDisabled) {
            e.preventDefault();
            return;
          }
          void onToggle();
        }}
        aria-disabled={buttonDisabled || undefined}
        className="doi-btn"
        style={{
          position: "relative",
          width: "100%",
          padding: 0,
          height: 44,
          border: `2px solid ${theme.panelEdgeStrong}`,
          background: theme.bgDeep,
          color: theme.ink,
          fontFamily: theme.fontDisplay,
          fontSize: 12,
          letterSpacing: theme.letterSpacing,
          cursor: buttonDisabled ? "not-allowed" : "pointer",
          opacity: buttonDisabled ? 0.7 : 1,
          overflow: "hidden",
          textAlign: "left",
        }}
      >
        {/* Filled portion of the bar */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            width: `${fillPct}%`,
            background: atFull ? E32.forest : E32.rust,
            transition: "width 200ms linear",
          }}
        />
        {/* Foreground content: HP value left, action label right.
            White (not parchment) so the text reads cleanly over BOTH
            the colored fill bar and any unfilled parchment background
            when the bar is partial — parchment-on-parchment is the
            "transparent" regression the bug was about. */}
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: "100%",
            padding: "0 14px",
            gap: 12,
            color: E32.white,
            textShadow: `1px 1px 0 ${theme.panelEdgeStrong}`,
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontSize: 14,
            }}
          >
            <HeartIcon size={14} />
            {hp}/{state.max_hp}
          </span>
          <span style={{ fontSize: 12 }}>{buttonLabel.toUpperCase()}</span>
        </div>
      </button>

      {error && (
        <div
          style={{
            color: E32.rust,
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: theme.letterSpacing,
          }}
        >
          {error}
        </div>
      )}

      {state.sleeping && (
        <div
          style={{
            fontSize: 12,
            color: theme.inkFaint,
            fontStyle: "italic",
          }}
        >
          You can't go anywhere else in the world tree while resting. Stop
          sleeping to leave the Inn.
        </div>
      )}
    </div>
  );
}
